"use client";

import L from "leaflet";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { INDIA_BOUNDS } from "@/lib/devices/fleet-dashboard";

import "leaflet/dist/leaflet.css";

const DEFAULT_CENTER = L.latLng(30.738708, 76.78626);
const DEFAULT_ZOOM = 11;
const INDIA_BOUNDS_LL = L.latLngBounds(
  L.latLng(INDIA_BOUNDS.south, INDIA_BOUNDS.west),
  L.latLng(INDIA_BOUNDS.north, INDIA_BOUNDS.east),
);

type LocationPickMapProps = {
  latitude: string;
  longitude: string;
  onPick: (lat: number, lng: number) => void;
  className?: string;
};

function parseCoord(s: string): number | null {
  const n = Number(String(s).trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Small Leaflet map: click to set coordinates (India-focused defaults). Keeps panning within India bounds.
 */
export function LocationPickMap({ latitude, longitude, onPick, className }: LocationPickMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.CircleMarker | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const syncMarker = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const lat = parseCoord(latitude);
    const lng = parseCoord(longitude);
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (lat == null || lng == null) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
      return;
    }

    const ll = L.latLng(lat, lng);
    const cm = L.circleMarker(ll, {
      radius: 8,
      color: "var(--accent)",
      fillColor: "var(--viz-series-1)",
      fillOpacity: 0.9,
      weight: 2,
    });
    cm.addTo(map);
    markerRef.current = cm;
    map.setView(ll, Math.max(map.getZoom(), 8), { animate: true });
  }, [latitude, longitude]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const map = L.map(el, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
      minZoom: 4,
      maxBounds: INDIA_BOUNDS_LL.pad(0.08),
      maxBoundsViscosity: 0.85,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      onPickRef.current(e.latlng.lat, e.latlng.lng);
    });

    mapRef.current = map;
    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
    window.setTimeout(() => {
      map.invalidateSize();
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
    }, 0);

    const onResize = () => {
      map.invalidateSize();
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
      }
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    syncMarker();
  }, [syncMarker]);

  return (
    <div
      className={
        className ??
        "relative overflow-hidden rounded-control border border-primary/15 bg-surface-inset"
      }
    >
      <div
        ref={containerRef}
        className="h-full min-h-[14rem] w-full [&_.leaflet-container]:!h-full [&_.leaflet-container]:!w-full"
        aria-label="Pick location on map"
      />
    </div>
  );
}
